import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, X, Clock, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_STYLE = {
    Submitted: "bg-gold-500/10 text-gold-700 border-gold-200",
    InProgress: "bg-blue-50 text-blue-700 border-blue-200",
    InForce: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Closed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Expired: "bg-navy-50 text-navy-500 border-navy-200",
    Declined: "bg-red-50 text-red-700 border-red-200",
};

const STATUS_OPTIONS = ["Submitted", "InProgress", "InForce", "Expired", "Declined", "Closed"];

// Same coverage-summary logic as the backend's vendor-notification email
// (server.py's _calculator_email_block) — kept in sync intentionally so a
// broker sees the same breakdown here as in the email that alerted them.
function memberCoverage(m) {
    const bits = [];
    if (m.hs_enabled) {
        bits.push(`Hospital & Surgery — ${m.hs_plan_room || "—"} (${m.hs_deductible || "—"} deductible)`);
    }
    if (m.op_enabled) {
        bits.push(`Outpatient — ${m.op_plan || "—"} (${m.op_cap || "—"})`);
    }
    if (m.dental_enabled) {
        bits.push(`Dental — ${m.dental_plan || "—"}`);
    }
    return bits.length ? bits : ["No coverage selected"];
}

const toDateInput = (iso) => (iso ? iso.slice(0, 10) : "");

export default function ApplicationDetail() {
    const { appId } = useParams();
    const navigate = useNavigate();
    const [app, setApp] = useState(null);
    const [form, setForm] = useState({
        application_status: "Submitted",
        policy_in_force_at: null,
        policy_expiry_at: "",
        charged_annual_premium: "",
        charged_quarterly_premium: "",
        charged_monthly_premium: "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get(`/broker/applications/${appId}`)
            .then((r) => {
                setApp(r.data);
                setForm({
                    application_status: r.data.application_status || "Submitted",
                    policy_in_force_at: r.data.policy_in_force_at || null,
                    policy_expiry_at: toDateInput(r.data.policy_expiry_at),
                    charged_annual_premium: r.data.charged_annual_premium ?? "",
                    charged_quarterly_premium: r.data.charged_quarterly_premium ?? "",
                    charged_monthly_premium: r.data.charged_monthly_premium ?? "",
                    notes: r.data.notes || "",
                });
            })
            .catch((e) => toast.error(formatApiError(e)));
    }, [appId]);

    if (!app) return <div className="p-10 text-navy-500">Loading…</div>;

    const num = (v) => (v === "" || v == null ? null : Number(v));

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                application_status: form.application_status,
                policy_in_force_at: form.policy_in_force_at || null,
                policy_expiry_at: form.policy_expiry_at
                    ? new Date(form.policy_expiry_at).toISOString()
                    : null,
                charged_annual_premium: num(form.charged_annual_premium),
                charged_quarterly_premium: num(form.charged_quarterly_premium),
                charged_monthly_premium: num(form.charged_monthly_premium),
                notes: form.notes,
            };
            const { data } = await api.patch(
                `/broker/applications/${appId}`,
                payload
            );
            setApp(data);
            toast.success("Application updated");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const quickDecide = async (application_status) => {
        setForm((f) => ({ ...f, application_status }));
        try {
            const { data } = await api.patch(`/broker/applications/${appId}`, { application_status });
            setApp(data);
            toast.success(`Application marked ${application_status}`);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const members = app.calculator_inputs?.members || [];
    const currency = (app.currency || "hkd").toUpperCase();

    return (
        <>
            <div className="px-6 lg:px-12 pt-6">
                <Link
                    to="/broker/applications"
                    data-testid="app-back"
                    className="inline-flex items-center gap-2 text-sm text-navy-500 hover:text-navy-700"
                >
                    <ArrowLeft size={15} /> All applications
                </Link>
            </div>

            <PageHeader
                eyebrow={`Application · ${appId}`}
                title={app.member?.name || "Household member"}
                description={`${app.product_name} · submitted ${app.created_at ? new Date(app.created_at).toLocaleDateString() : "—"}`}
                action={
                    <Badge className={`${STATUS_STYLE[app.application_status] || ""} border text-sm px-3 py-1`}>
                        {app.application_status}
                    </Badge>
                }
                testid="app-detail-header"
            />

            <div className="px-6 lg:px-12 py-8 grid lg:grid-cols-[1fr_320px] gap-8 items-start">
                <form onSubmit={save} className="rounded-2xl bg-white shadow-soft p-8 space-y-6" data-testid="app-form">
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">Applicant</Label>
                            <div className="mt-2 font-medium text-navy-700">{app.member?.name || "—"}</div>
                            <div className="text-sm text-navy-500">{app.member?.email || app.buyer_email}</div>
                        </div>
                        <div>
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">Product</Label>
                            <div className="mt-2 font-medium text-navy-700">{app.product_name}</div>
                            <div className="text-sm text-navy-500">
                                Indicative premium:{" "}
                                {app.calculator_quote != null
                                    ? `${currency} ${Number(app.calculator_quote).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    : "—"}
                            </div>
                        </div>
                    </div>

                    {members.length > 0 && (
                        <div>
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                Covered members ({members.length})
                            </Label>
                            <div className="mt-2 space-y-2">
                                {members.map((m, i) => (
                                    <div
                                        key={i}
                                        data-testid={`app-member-${i}`}
                                        className="rounded-lg bg-cream p-3 text-sm"
                                    >
                                        <div className="font-medium text-navy-700">
                                            {m.name || `Member ${i + 1}`}
                                            {m.age != null && (
                                                <span className="text-navy-500 font-normal"> · age {m.age}</span>
                                            )}
                                        </div>
                                        <div className="text-navy-500 text-xs mt-0.5">
                                            {memberCoverage(m).join(" · ")}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid sm:grid-cols-3 gap-5">
                        <div>
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">Charged annual premium</Label>
                            <Input
                                data-testid="app-charged-annual"
                                type="number"
                                step="0.01"
                                value={form.charged_annual_premium}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, charged_annual_premium: e.target.value }))
                                }
                                className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                            />
                        </div>
                        <div>
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">Charged quarterly premium</Label>
                            <Input
                                data-testid="app-charged-quarterly"
                                type="number"
                                step="0.01"
                                value={form.charged_quarterly_premium}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, charged_quarterly_premium: e.target.value }))
                                }
                                className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                            />
                        </div>
                        <div>
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">Charged monthly premium</Label>
                            <Input
                                data-testid="app-charged-monthly"
                                type="number"
                                step="0.01"
                                value={form.charged_monthly_premium}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, charged_monthly_premium: e.target.value }))
                                }
                                className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                            />
                        </div>
                    </div>

                    <div className="grid sm:grid-cols-3 gap-5 items-end">
                        <div>
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">Status</Label>
                            <Select
                                value={form.application_status}
                                onValueChange={(v) => setForm((f) => ({ ...f, application_status: v }))}
                            >
                                <SelectTrigger data-testid="app-status" className="mt-2 bg-white border-navy-100 h-11">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    {STATUS_OPTIONS.map((s) => (
                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-navy-100 h-11 px-3">
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">Policy in force</Label>
                            <Switch
                                data-testid="app-policy-in-force"
                                checked={Boolean(form.policy_in_force_at)}
                                onCheckedChange={(v) =>
                                    setForm((f) => ({
                                        ...f,
                                        policy_in_force_at: v ? new Date().toISOString() : null,
                                    }))
                                }
                            />
                        </div>
                        <div>
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">Policy expiry</Label>
                            <Input
                                data-testid="app-policy-expiry"
                                type="date"
                                value={form.policy_expiry_at}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, policy_expiry_at: e.target.value }))
                                }
                                className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="text-navy-700 text-xs uppercase tracking-widest">Notes</Label>
                        <Textarea
                            data-testid="app-notes"
                            rows={4}
                            value={form.notes}
                            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                            className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500"
                            placeholder="Any conditions, underwriting notes, follow-up context…"
                        />
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => navigate("/broker/applications")}
                            className="text-navy-500"
                        >
                            Back
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid="app-save"
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6"
                        >
                            <Save size={15} className="mr-2" /> {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                </form>

                <aside className="rounded-2xl bg-white shadow-soft p-6 space-y-3">
                    <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-2">
                        Quick decision
                    </div>
                    <Button
                        data-testid="app-approve"
                        onClick={() => quickDecide("InForce")}
                        disabled={app.application_status === "InForce"}
                        className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-11"
                    >
                        <Check size={15} className="mr-2" /> In force
                    </Button>
                    <Button
                        data-testid="app-hold"
                        onClick={() => quickDecide("InProgress")}
                        variant="outline"
                        className="w-full rounded-full border-navy-100 text-navy-700 hover:bg-navy-50 h-11"
                    >
                        <Clock size={15} className="mr-2" /> Hold
                    </Button>
                    <Button
                        data-testid="app-reject"
                        onClick={() => quickDecide("Declined")}
                        variant="outline"
                        className="w-full rounded-full border-red-200 text-red-600 hover:bg-red-50 h-11"
                    >
                        <X size={15} className="mr-2" /> Decline
                    </Button>
                </aside>
            </div>
        </>
    );
}
